/**
 * Lectura tolerante de JSON del backend: la API de Materialidad responde en PascalCase, pero algunos
 * cuerpos (el `Datos` de ciertos avisos del buzón, el middleware de errores) viajan en camelCase.
 * Estas funciones buscan la clave sin importar la capitalización de la primera letra.
 */

/** Objeto plano (no arreglo, no `null`) o `null`. */
export const comoObjeto = (valor: unknown): Record<string, unknown> | null =>
  valor !== null && typeof valor === 'object' && !Array.isArray(valor) ? (valor as Record<string, unknown>) : null;

/**
 * @function leerCampo
 * @description Valor de `nombre` en un objeto, probando `Nombre` y `nombre`. `undefined` si no está o si
 * `origen` no es un objeto.
 */
export const leerCampo = (origen: unknown, nombre: string): unknown => {
  const objeto = comoObjeto(origen);
  if (!objeto || !nombre) {
    return undefined;
  }
  const pascal = nombre.charAt(0).toUpperCase() + nombre.slice(1);
  const camel = nombre.charAt(0).toLowerCase() + nombre.slice(1);
  if (pascal in objeto) {
    return objeto[pascal];
  }
  return camel in objeto ? objeto[camel] : undefined;
};

/** Texto del campo, o `null` si no es una cadena. */
export const leerTexto = (origen: unknown, nombre: string): string | null => {
  const valor = leerCampo(origen, nombre);
  return typeof valor === 'string' ? valor : null;
};

/** Número finito del campo (acepta cadenas numéricas), o `null`. */
export const leerNumero = (origen: unknown, nombre: string): number | null => {
  const valor = leerCampo(origen, nombre);
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : null;
  }
  if (typeof valor === 'string' && valor.trim() !== '') {
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : null;
  }
  return null;
};

/** Booleano del campo, o `null` si no es booleano. */
export const leerBooleano = (origen: unknown, nombre: string): boolean | null => {
  const valor = leerCampo(origen, nombre);
  return typeof valor === 'boolean' ? valor : null;
};

/** Arreglo del campo, o `[]` si no es un arreglo. */
export const leerLista = (origen: unknown, nombre: string): unknown[] => {
  const valor = leerCampo(origen, nombre);
  return Array.isArray(valor) ? valor : [];
};
